import VoiceAgentApi from "@/Apis/VoiceAgentApi";

const devanagariPattern = /[ऀ-ॿ]/;
const mojibakePattern = /(?:à¤|à¥|Ù|Û|Ø|ã|Ã)/;

export function cleanTranscriptText(text, role = "customer") {
  if (!text) return "";
  if (devanagariPattern.test(text)) {
    return role === "agent"
      ? "Maazrat, main sirf English ya Roman Urdu mein jawab de sakti hoon."
      : "Customer ne Urdu/Hindi mein baat ki. Transcript Roman Urdu/English mein allowed hai.";
  }
  if (mojibakePattern.test(text)) {
    return role === "agent"
      ? "Maazrat, text encoding clear nahi tha. Main English ya Roman Urdu mein jawab dungi."
      : "Customer audio received. Transcript clear nahi tha, please English ya Roman Urdu mein repeat karein.";
  }
  return text;
}

export class RealtimeVoiceSession {
  constructor({ customer, onStatus, onMessage, onError, onDisconnect }) {
    this.customer = customer;
    this.onStatus = onStatus;
    this.onMessage = onMessage;
    this.onError = onError;
    this.onDisconnect = onDisconnect;
    this.messages = [];
    this.pendingCalls = new Map();
    this.completedCalls = new Set();
    this.micMuted = false;
  }

  setMicMuted(muted) {
    this.micMuted = muted;
    this.stream?.getAudioTracks().forEach((track) => {
      track.enabled = !muted;
    });
  }

  async connect() {
    this.onStatus("Requesting microphone access");

    const { data: tokenData } = await VoiceAgentApi.createRealtimeSession(this.customer);
    const ephemeralKey = tokenData.value || tokenData.client_secret?.value;
    if (!ephemeralKey) throw new Error("Realtime session did not return a client secret");

    this.pc = new RTCPeerConnection();
    this.pc.onconnectionstatechange = () => {
      const state = this.pc?.connectionState;
      if (state === "failed" || state === "disconnected" || state === "closed") {
        this.onStatus(state === "failed" ? "Connection failed" : "Disconnected");
        this.onDisconnect?.();
      }
    };
    this.audio = document.createElement("audio");
    this.audio.autoplay = true;
    this.pc.ontrack = (event) => {
      this.audio.srcObject = event.streams[0];
    };

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
      },
      video: false,
    });
    this.stream.getAudioTracks().forEach((track) => {
      track.onended = () => {
        this.onStatus("Microphone stopped");
        this.onDisconnect?.();
      };
    });
    this.stream.getTracks().forEach((track) => this.pc.addTrack(track, this.stream));

    this.dc = this.pc.createDataChannel("oai-events");
    this.dc.onopen = () => {
      this.onStatus("Connected");
      this.send({
        type: "response.create",
        response: {
          instructions: `Greet ${this.customer.customerName} as the store AI CX Agent in English or Roman Urdu only. Keep it under 2 sentences and ask how you can help.`,
        },
      });
    };
    this.dc.onclose = () => {
      this.onStatus("Disconnected");
      this.onDisconnect?.();
    };
    this.dc.onerror = () => {
      this.onError?.("Voice connection error. Please reconnect.");
      this.onDisconnect?.();
    };
    this.dc.onmessage = (event) => this.handleEvent(JSON.parse(event.data));

    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);

    const sdpResponse = await fetch("https://api.openai.com/v1/realtime/calls", {
      method: "POST",
      body: offer.sdp,
      headers: {
        Authorization: `Bearer ${ephemeralKey}`,
        "Content-Type": "application/sdp",
      },
    });

    if (!sdpResponse.ok) {
      throw new Error(await sdpResponse.text());
    }

    await this.pc.setRemoteDescription({
      type: "answer",
      sdp: await sdpResponse.text(),
    });
  }

  send(event) {
    if (this.dc?.readyState === "open") {
      this.dc.send(JSON.stringify(event));
    }
  }

  handleEvent(event) {
    if (event.type === "conversation.item.input_audio_transcription.completed" && event.transcript) {
      this.pushMessage("customer", event.transcript);
      this.autoLookupOrderFromTranscript(event.transcript);
    }

    if (
      (event.type === "response.output_text.delta" || event.type === "response.audio_transcript.delta") &&
      event.delta
    ) {
      this.agentDraft = `${this.agentDraft || ""}${event.delta}`;
      this.onMessage({ role: "agent", text: this.agentDraft, draft: true });
    }

    if (event.type === "response.audio_transcript.done" || event.type === "response.output_text.done") {
      if (this.agentDraft) {
        this.pushMessage("agent", this.agentDraft);
        this.agentDraft = "";
      }
    }

    if (event.type === "response.function_call_arguments.delta") {
      const current = this.pendingCalls.get(event.call_id) || { name: event.name, args: "" };
      current.args += event.delta || "";
      if (event.name) current.name = event.name;
      this.pendingCalls.set(event.call_id, current);
    }

    if (event.type === "response.function_call_arguments.done") {
      const pending = this.pendingCalls.get(event.call_id) || {
        name: event.name,
        args: event.arguments || "{}",
      };
      pending.args = event.arguments || pending.args || "{}";
      pending.name = event.name || pending.name;
      this.pendingCalls.delete(event.call_id);
      this.executeTool(event.call_id, pending.name, pending.args);
    }

    if (event.type === "response.output_item.done" && event.item?.type === "function_call") {
      this.executeTool(event.item.call_id, event.item.name, event.item.arguments || "{}");
    }

    if (event.type === "error") {
      this.onError?.(event.error?.message || "Realtime error");
    }
  }

  pushMessage(role, text) {
    const message = { role, text: cleanTranscriptText(text, role), at: new Date().toISOString() };
    this.messages.push(message);
    this.onMessage(message);
  }

  pushProducts(query, products) {
    const message = {
      role: "agent",
      type: "products",
      query,
      products,
      text: `Showing ${products.length} product${products.length === 1 ? "" : "s"} for "${query}"`,
      at: new Date().toISOString(),
    };
    this.messages.push(message);
    this.onMessage(message);
  }

  async executeTool(callId, name, rawArgs) {
    if (!callId || this.completedCalls.has(callId)) return;
    this.completedCalls.add(callId);

    try {
      const args = JSON.parse(rawArgs || "{}");
      let result;

      if (name === "search_knowledge_base") {
        result = await this.withTimeout(
          VoiceAgentApi.searchKnowledge({ query: args.query, includeAnswer: false }).then((r) => r.data),
          8000,
          "Knowledge search took too long. Tell the customer you will create a support ticket if needed."
        );
        result = this.formatKnowledgeToolResult(result);
      } else if (name === "get_order_status") {
        const orderNumber = this.extractOrderNumber(args, rawArgs);
        if (!orderNumber) {
          result = {
            error: "Order number was not clear. Ask the customer to repeat the order number digit by digit.",
          };
        } else {
          result = await this.withTimeout(
            VoiceAgentApi.getOrderStatus(orderNumber)
              .then((r) => r.data)
              .catch((e) => ({
                error:
                  e?.response?.status === 404
                    ? "Order not found"
                    : e.message || "Order lookup failed",
              })),
            6000,
            "Order lookup timed out."
          );
        }
      } else if (name === "search_products") {
        const query = args.query || args.q || args.productName || "";
        if (!query) {
          result = { error: "Product name was not clear. Ask the customer which product they mean." };
        } else {
          result = await this.withTimeout(
            VoiceAgentApi.searchProducts(query, args.limit || 5)
              .then((r) => {
                const products = r.data?.products || [];
                if (products.length > 0) {
                  this.pushProducts(query, products);
                }
                return this.formatProductToolResult(r.data);
              })
              .catch((e) => ({
                error: e?.response?.data?.detail || e.message || "Product search failed",
              })),
            6000,
            "Product search timed out."
          );
        }
      } else if (name === "create_support_ticket") {
        result = await this.withTimeout(
          VoiceAgentApi.createTicket({ ...args, ...this.customer }).then((r) => r.data),
          6000,
          "Ticket creation timed out."
        );
      } else if (name === "request_human_agent") {
        result = await this.withTimeout(
          VoiceAgentApi.createHandoff({ ...args, ...this.customer }).then((r) => r.data),
          6000,
          "Human handoff request timed out."
        );
      } else {
        result = { error: `Unknown tool: ${name}` };
      }

      this.send({
        type: "conversation.item.create",
        item: {
          type: "function_call_output",
          call_id: callId,
          output: JSON.stringify(result),
        },
      });
      this.send({ type: "response.create" });
    } catch (error) {
      this.send({
        type: "conversation.item.create",
        item: {
          type: "function_call_output",
          call_id: callId,
          output: JSON.stringify({
            error: error?.response?.data?.detail || error.message,
          }),
        },
      });
      this.send({ type: "response.create" });
    }
  }

  async autoLookupOrderFromTranscript(transcript) {
    const orderNumber = this.extractOrderNumber({}, transcript);
    if (!orderNumber || orderNumber.length < 3) return;

    const text = String(transcript).toLowerCase();
    const looksLikeOrderIntent =
      /order|tracking|track|status|delivery|parcel|shipment|mera|meri|number|numbr|nambar|order number/i.test(
        text
      );
    if (!looksLikeOrderIntent) return;

    if (this.lastAutoOrderLookup === orderNumber) return;
    this.lastAutoOrderLookup = orderNumber;

    try {
      const { data: result } = await VoiceAgentApi.getOrderStatus(transcript);
      this.send({
        type: "response.create",
        response: {
          instructions: `The customer provided order number ${orderNumber}. Backend order lookup result: ${JSON.stringify(result)}. Reply naturally in English or Roman Urdu only. Mention status, payment status, and delivery ETA. Keep it concise.`,
        },
      });
    } catch (error) {
      this.send({
        type: "response.create",
        response: {
          instructions: `The customer provided order number ${orderNumber}, but backend lookup did not find it or failed. Ask the customer to repeat the order number digit by digit before creating a ticket.`,
        },
      });
    }
  }

  async withTimeout(promise, timeoutMs, timeoutMessage) {
    let timeoutId;
    const timeout = new Promise((resolve) => {
      timeoutId = setTimeout(() => resolve({ warning: timeoutMessage }), timeoutMs);
    });
    const result = await Promise.race([promise, timeout]);
    clearTimeout(timeoutId);
    return result;
  }

  formatProductToolResult(data) {
    const products = (data?.products || []).map((product) => ({
      title: product.title,
      brand: product.brand,
      price: product.price,
      compareAtPrice: product.compareAtPrice,
      inStock: product.inStock,
      variants: product.variants,
    }));

    if (products.length === 0) {
      return {
        found: false,
        instruction:
          "No matching product was found. Tell the customer politely and offer to create a support ticket. Do not invent a product or price.",
      };
    }

    return {
      found: true,
      count: products.length,
      products,
      instruction:
        "Name the product, its price, and whether it is in stock. Mention at most 2 or 3 products. Use English or Roman Urdu only. Keep it under 30 words.",
    };
  }

  formatKnowledgeToolResult(result) {
    if (result?.warning || result?.error) return result;

    const answer = result?.answer?.answer || "";
    const snippets = (result?.results || []).slice(0, 3).map((item) => ({
      filename: item.filename,
      score: item.score,
      text: item.text?.slice(0, 650),
    }));

    return {
      found: Boolean(answer || snippets.length),
      answer,
      sources: snippets,
      instruction:
        "Answer from these snippets only. Use English or Roman Urdu only. Keep it under 25 words unless the customer asks for detail.",
    };
  }

  extractOrderNumber(args, rawArgs) {
    const candidates = [
      args.orderNumber,
      args.order_number,
      args.orderId,
      args.order_id,
      args.id,
      args.number,
      rawArgs,
    ].filter(Boolean);

    for (const candidate of candidates) {
      const text = String(candidate);
      const alphaNumeric = text.match(/[a-zA-Z]{1,5}[-\s]?\d{3,}|\d{3,}/);
      if (alphaNumeric) return alphaNumeric[0].replace(/\s+/g, "");

      const spokenDigits = this.spokenDigitsToNumber(text);
      if (spokenDigits.length >= 3) return spokenDigits;
    }

    return "";
  }

  spokenDigitsToNumber(text) {
    const digitWords = {
      zero: "0",
      oh: "0",
      o: "0",
      one: "1",
      won: "1",
      two: "2",
      too: "2",
      to: "2",
      three: "3",
      tree: "3",
      four: "4",
      for: "4",
      five: "5",
      six: "6",
      seven: "7",
      eight: "8",
      ate: "8",
      nine: "9",
      ek: "1",
      aik: "1",
      do: "2",
      teen: "3",
      char: "4",
      chaar: "4",
      panch: "5",
      paanch: "5",
      che: "6",
      chay: "6",
      chhe: "6",
      saat: "7",
      sat: "7",
      aath: "8",
      ath: "8",
      nau: "9",
      no: "9",
    };

    const tokens = String(text)
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, " ")
      .split(/\s+/)
      .filter(Boolean);

    let output = "";
    let repeatCount = 1;
    for (const token of tokens) {
      if (token === "double") {
        repeatCount = 2;
        continue;
      }
      if (token === "triple") {
        repeatCount = 3;
        continue;
      }
      if (/^\d+$/.test(token)) {
        output += token;
        repeatCount = 1;
        continue;
      }
      const digit = digitWords[token];
      if (digit) {
        output += digit.repeat(repeatCount);
        repeatCount = 1;
      }
    }

    return output;
  }

  async end(status = "ended") {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.dc?.close();
    this.pc?.close();
    this.onStatus("Ended");

    if (this.messages.length > 0) {
      await VoiceAgentApi.saveChat({
        ...this.customer,
        status,
        messages: this.messages.map((message) => ({
          role: message.role,
          text: message.text,
        })),
      }).catch(() => null);
    }
  }
}
